/**
 * useContractReferenceData Hook
 * Loads all reference data needed for contract forms
 */

import { useEffect, useCallback } from 'react';
import { apiGet, extractApiData } from '../utils/apiClient';
import { cachedApiGet } from '../utils/refDataCache';
import { useContractFormContext } from '../components/contracts/wizard/ContractFormContext';
import logger from '../utils/logger';

export function useContractReferenceData() {
  const {
    setRefData,
    setRefDataLoading,
    setRefDataError,
    refData,
    refDataLoading,
    refDataError,
  } = useContractFormContext();

  const loadReferenceData = useCallback(async () => {
    try {
      setRefDataLoading(true);
      setRefDataError(null);

      const [
        investorsData,
        productsData,
        frequenciesData,
        interestTypesData,
        interestRatesData,
        placementFeesData,
        brokersData,
        providersData,
        issuersData,
        collateralTypesData,
        currenciesData,
        calculationProfilesData,
        commissionRatesData,
        vatRatesData,
      ] = await Promise.all([
        // Mutable feeds (change when a user adds an investor/rate mid-session) stay on
        // plain apiGet so a create is reflected immediately.
        apiGet('/stakeholders/investors/select'),
        // Near-static reference config: cachedApiGet (5-min TTL, tenant-scoped key,
        // in-flight dedupe) collapses repeat form mounts to 0 calls.
        cachedApiGet('/refdata/investment-types'),
        cachedApiGet('/refdata/frequencies'),
        cachedApiGet('/refdata/interest-types?active=true'),
        apiGet('/refdata/interest-rates'),
        cachedApiGet('/refdata/placement-fees'),
        apiGet('/stakeholders/brokers/select'),
        apiGet('/stakeholders/collateral-providers/select'),
        apiGet('/stakeholders/issuers/select').catch(() => ({ data: [] })),
        cachedApiGet('/refdata/collateral-types'),
        cachedApiGet('/refdata/countries'),
        cachedApiGet('/refdata/calculation-profiles'),
        apiGet('/refdata/commission-rates'),
        cachedApiGet('/refdata/vat-rates'),
      ]);

      setRefData({
        investors: extractApiData(investorsData),
        products: extractApiData(productsData),
        frequencies: extractApiData(frequenciesData),
        interestTypes: extractApiData(interestTypesData),
        interestRates: extractApiData(interestRatesData),
        placementFees: extractApiData(placementFeesData),
        brokers: extractApiData(brokersData),
        collateralProviders: extractApiData(providersData),
        issuers: extractApiData(issuersData),
        collateralTypes: extractApiData(collateralTypesData),
        currencies: extractApiData(currenciesData),
        calculationProfiles: extractApiData(calculationProfilesData),
        commissionRates: extractApiData(commissionRatesData),
        vatRates: extractApiData(vatRatesData),
      });
    } catch (err) {
      logger.error('Error loading reference data', { message: err.message });
      setRefDataError('Failed to load reference data. Please refresh the page or contact support.');
    } finally {
      setRefDataLoading(false);
    }
  }, [setRefData, setRefDataLoading, setRefDataError]);

  useEffect(() => {
    loadReferenceData();
  }, [loadReferenceData]);

  return {
    ...refData,
    loading: refDataLoading,
    error: refDataError,
    reload: loadReferenceData,
  };
}

export default useContractReferenceData;
